/**
 * Cascading Speciality → Level → Module → Course picker.
 *
 * Each dropdown is a searchable combobox — trainers type to filter
 * (case-insensitive, matches label + hint), arrow-key navigate, Enter
 * to select, Escape to close. Choosing a parent clears deeper
 * selections so stale IDs can't leak into the upload metadata.
 *
 * Data source: the deduplicated /api/v1/ai/taxonomy tree served by
 * server_api (proxied through /v1/taxonomy on ai_training).
 */
import React, { useMemo } from 'react';
import { BookOpen, GraduationCap, Layers, Library } from 'lucide-react';
import type { Taxonomy, PendingUpload } from './types';
import { SearchableSelect, type Option } from './SearchableSelect';

interface Props {
  taxonomy: Taxonomy | null;
  loading:  boolean;
  value:    PendingUpload;
  onChange: (patch: Partial<PendingUpload>) => void;
  /** How deep the cascade needs to go. Curricula are scoped to a
   *  Speciality → Level (the CDACC unit standard covers every course
   *  in that level), so Module + Course are irrelevant and hidden.
   *  Defaults to the full 'course' depth used by past-paper uploads. */
  depth?: 'level' | 'module' | 'course';
}

export const TaxonomyPicker: React.FC<Props> = ({ taxonomy, loading, value, onChange, depth = 'course' }) => {
  const showModule = depth === 'module' || depth === 'course';
  const showCourse = depth === 'course';
  const specs = taxonomy?.specialities ?? [];

  const currentSpec = useMemo(
    () => specs.find((s) => s.key === value.speciality_key) || null,
    [specs, value.speciality_key],
  );
  const currentLevel = useMemo(
    () => currentSpec?.levels.find((l) => l.key === value.level_key) || null,
    [currentSpec, value.level_key],
  );
  const currentModule = useMemo(
    () => currentLevel?.modules.find((m) => m.key === value.module_key) || null,
    [currentLevel, value.module_key],
  );

  const specOptions: Option[] = useMemo(
    () => specs.map((s) => ({ value: s.key, label: s.name })),
    [specs],
  );
  const levelOptions: Option[] = useMemo(
    () => (currentSpec?.levels ?? []).map((l) => ({
      value: l.key,
      label: l.name,
      hint:  l.level_number != null ? `Level ${l.level_number}` : undefined,
    })),
    [currentSpec],
  );
  const moduleOptions: Option[] = useMemo(
    () => (currentLevel?.modules ?? []).map((m) => ({
      value: m.key,
      label: m.title,
    })),
    [currentLevel],
  );
  const courseOptions: Option[] = useMemo(
    () => (currentModule?.courses ?? []).map((c) => ({
      value: c.key,
      label: c.code,
      hint:  c.title,
    })),
    [currentModule],
  );

  const setSpec = (key: string | undefined) => {
    const s = specs.find((x) => x.key === key);
    onChange({
      speciality_key:  s?.key,
      speciality_name: s?.name,
      level_key: undefined, level_name: undefined,
      module_key: undefined, module_title: undefined,
      course_id: undefined, course_code: undefined, course_title: undefined,
    });
  };

  const setLevel = (key: string | undefined) => {
    const l = currentSpec?.levels.find((x) => x.key === key);
    onChange({
      level_key:  l?.key,
      level_name: l?.name,
      module_key: undefined, module_title: undefined,
      course_id: undefined, course_code: undefined, course_title: undefined,
    });
  };

  const setModule = (key: string | undefined) => {
    const m = currentLevel?.modules.find((x) => x.key === key);
    onChange({
      module_key:   m?.key,
      module_title: m?.title,
      course_id: undefined, course_code: undefined, course_title: undefined,
    });
  };

  const setCourse = (key: string | undefined) => {
    const c = currentModule?.courses.find((x) => x.key === key);
    onChange({
      course_id:    c?.key,
      course_code:  c?.code,
      course_title: c?.title,
    });
  };

  if (loading && !taxonomy) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
        Loading curriculum tree…
      </div>
    );
  }
  if (!specs.length) {
    return (
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-3 py-4 text-[12px] text-amber-800">
        No curriculum data reached the trainer service. The tree is served
        from the main LMS — check that /api/v1/ai/taxonomy is reachable.
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {/* Row 1: Speciality (full width, searchable) */}
      <PickerField icon={<GraduationCap size={11} />} label="Speciality">
        <SearchableSelect
          options={specOptions}
          value={value.speciality_key}
          onChange={setSpec}
          placeholder="Search specialities…"
        />
      </PickerField>

      {/* Row 2: Level (+ Module, when this depth needs it) */}
      <div className={showModule ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2'}>
        <PickerField icon={<Layers size={11} />} label="Level">
          <SearchableSelect
            options={levelOptions}
            value={value.level_key}
            onChange={setLevel}
            disabled={!currentSpec}
            placeholder={currentSpec ? 'Search levels…' : 'Pick speciality first'}
          />
        </PickerField>

        {showModule && (
          <PickerField icon={<Library size={11} />} label="Module">
            <SearchableSelect
              options={moduleOptions}
              value={value.module_key}
              onChange={setModule}
              disabled={!currentLevel}
              placeholder={currentLevel ? 'Search modules…' : 'Pick level first'}
            />
          </PickerField>
        )}
      </div>

      {/* Row 3: Course — omitted for curriculum uploads, which are
          scoped to a whole Speciality/Level, not a single course. */}
      {showCourse && (
        <PickerField icon={<BookOpen size={11} />} label="Unit / Course">
          <SearchableSelect
            options={courseOptions}
            value={value.course_id}
            onChange={setCourse}
            disabled={!currentModule}
            placeholder={currentModule ? 'Search courses…' : 'Pick module first'}
          />
        </PickerField>
      )}

      {/* Chosen path summary */}
      {depth === 'course' && value.course_id && (
        <div className="mt-1 flex items-start gap-1.5 rounded-lg bg-[#0e9172]/8 px-3 py-2 text-[11px] text-slate-700 ring-1 ring-[#0e9172]/15">
          <BookOpen size={11} className="mt-0.5 flex-shrink-0 text-[#0e9172]" />
          <div className="min-w-0">
            <span className="font-semibold text-[#0e9172]">Tagged as:</span>{' '}
            <span className="truncate">
              {value.speciality_name} · {value.level_name} · {value.module_title} · {value.course_code}
            </span>
          </div>
        </div>
      )}
      {depth === 'level' && value.level_key && (
        <div className="mt-1 flex items-start gap-1.5 rounded-lg bg-[#0e9172]/8 px-3 py-2 text-[11px] text-slate-700 ring-1 ring-[#0e9172]/15">
          <GraduationCap size={11} className="mt-0.5 flex-shrink-0 text-[#0e9172]" />
          <div className="min-w-0">
            <span className="font-semibold text-[#0e9172]">Grounds:</span>{' '}
            <span className="truncate">
              every course under {value.speciality_name} · {value.level_name}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const PickerField: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon, label, children,
}) => (
  <label className="block">
    <span className="mb-1 flex items-center gap-1 text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
      {icon}
      {label}
    </span>
    {children}
  </label>
);
